import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IoSend, IoSearch } from 'react-icons/io5';
import { FiLogOut } from 'react-icons/fi';
import { toast } from 'react-toastify';
import socketService from '../../../services/socketService';
import { 
  setActiveUsers, 
  addMessage, 
  setMessages, 
  updateMessageReadStatus, 
  setSelectedUser,
  resetChat,
  clearUnreadMessages,
  addUnreadMessages
} from '../chatSlice';
import { logout } from '../../../features/auth/authSlice';
import { formatTime } from '../../../utils/dateUtils';
import './Chat.css';

function Chat() {
  const dispatch = useDispatch();
  const { user } = useSelector(state => state.auth);
  const { selectedUser, messages: currentMessages, unreadMessages } = useSelector(state => state.chat);
  
  const [messageInput, setMessageInput] = useState('');
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [isTyping, setIsTyping] = useState(null);
  const [isChatFocused, setIsChatFocused] = useState(document.hasFocus());
  const [socketInitialized, setSocketInitialized] = useState(false);
  const [isUserInitialized, setIsUserInitialized] = useState(false);
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const messagesEndRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  // Initialize socket connection
  const initializeSocket = useCallback(async () => {
    if (!user?._id || socketInitialized) {  // Add socketInitialized check
      console.log('Skipping socket initialization - no user or already initialized');
      return;
    }

    const token = localStorage.getItem('userToken');
    if (!token) {
      console.log('No token available for socket initialization');
      return;
    }

    try {
      await socketService.connect(token);
      console.log('Socket connected, emitting user connected');
      
      const success = socketService.emitUserConnected({
        _id: user._id,
        username: user.username
      });

      if (success) {
        setSocketInitialized(true);
        setIsUserInitialized(true);
        console.log('Socket and user initialized successfully');
      }
    } catch (error) {
      console.error('Socket initialization failed:', error);
      setSocketInitialized(false);
      setIsUserInitialized(false);
    }
  }, [user, socketInitialized]); // Add socketInitialized to dependencies

  const loadChatUsers = useCallback(async () => {
    if (!user?._id) {
      console.log('No user data, skipping chat users load');
      return;
    }

    const token = localStorage.getItem('userToken');
    if (!token) {
      console.log('No token available for loading chats');
      return;
    }

    try {
      console.log('Loading chat users for:', user.username);
      const [usersResponse, unreadCountsResponse] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_URL}/api/users/chats`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }),
        fetch(`${process.env.REACT_APP_API_URL}/api/messages/unread-counts`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })
      ]);

      if (!usersResponse.ok || !unreadCountsResponse.ok) 
        throw new Error('Failed to fetch chat data');
      
      const [chatUsers, unreadCounts] = await Promise.all([
        usersResponse.json(),
        unreadCountsResponse.json()
      ]);

      console.log('Loaded chat users:', chatUsers);
      console.log('Loaded unread counts:', unreadCounts);
      
      setUsers(chatUsers);
      
      // Update Redux store with unread counts
      Object.entries(unreadCounts).forEach(([userId, count]) => {
        if (count > 0) {
          dispatch(addUnreadMessages({ userId, count }));
        }
      });
    } catch (error) {
      console.error('Error loading chat data:', error);
    }
  }, [user, dispatch]);

  const loadMessages = useCallback(async () => {
    if (!user?._id || !selectedUser?.userId || !socketInitialized) {
      console.log('Cannot load messages - missing requirements:', {
        hasUser: !!user?._id,
        hasSelectedUser: !!selectedUser?.userId,
        socketInitialized
      });
      return;
    }
    
    setIsLoadingMessages(true);
    try {
      const token = localStorage.getItem('userToken');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/messages/${selectedUser.userId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) throw new Error('Failed to fetch messages');
      
      const messages = await response.json();
      dispatch(setMessages(messages));
      setTimeout(scrollToBottom, 100);
    } catch (error) {
      console.error('Error loading messages:', error);
      toast.error('Failed to load messages');
    } finally {
      setIsLoadingMessages(false);
    }
  }, [selectedUser, user, socketInitialized, dispatch, scrollToBottom]);

  const handleUserSelect = useCallback((selectedUser) => {
    console.log('User selection attempted:', {
      selectedUserId: selectedUser?._id,
      currentUserId: user?._id,
      isInitialized: isUserInitialized,
      hasSocket: socketInitialized
    });  

    if (!user?._id) {
      console.log('No current user ID available');
      return;
    }

    if (!isUserInitialized || !socketInitialized) {
      console.log('Reinitializing socket connection...');
      initializeSocket().then(() => {
        dispatch(setSelectedUser({
          userId: selectedUser._id,
          username: selectedUser.username
        }));
        dispatch(clearUnreadMessages(selectedUser._id));
      });
      return;
    }

    dispatch(setSelectedUser({
      userId: selectedUser._id,
      username: selectedUser.username
    }));
    dispatch(clearUnreadMessages(selectedUser._id));
  }, [user?._id, isUserInitialized, socketInitialized, dispatch, initializeSocket]);

  const handleLogout = useCallback(() => {
    console.log('Logout initiated, cleaning up...');
    
    setSocketInitialized(false);
    setIsUserInitialized(false);
    setUsers([]);
    setMessageInput('');
    setIsLoadingMessages(false);
    setIsTyping(null);
    
    // Clean up socket last
    if (socketService.socket) {
      socketService.socket.off('message');
      socketService.socket.off('messageRead');
      socketService.socket.off('typing');
      socketService.socket.off('activeUsers');
      socketService.disconnect();
    }
    
    dispatch(resetChat());
    dispatch(logout());
    
    console.log('Cleanup completed');
  }, [dispatch]);

  const handleTypingInput = (e) => {
    setMessageInput(e.target.value);
    
    if (socketService.socket && selectedUser) {
      socketService.socket.emit('typing', {
        senderId: user._id,
        receiverId: selectedUser.userId,
        username: user.username,
        isTyping: true
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!messageInput.trim() || !selectedUser || !user || !socketService.socket) return;

    const messageData = {
      sender: user._id,
      receiver: selectedUser.userId,
      content: messageInput.trim()
    };

    try {
      socketService.socket.emit('message', messageData);
      setMessageInput('');
      socketService.socket.emit('typing', {
        senderId: user._id,
        receiverId: selectedUser.userId,
        username: user.username,
        isTyping: false
      });
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    }
  };

  const markMessageAsRead = useCallback(async (messageId) => {
    if (!user?._id || !selectedUser?.userId || !socketInitialized || !isChatFocused) return;

    try {
      const token = localStorage.getItem('userToken');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/messages/${messageId}/read`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            selectedUserId: selectedUser.userId,
            isChatFocused
          })
        }
      );

      if (!response.ok) throw new Error('Failed to mark message as read');
      
      const updatedMessage = await response.json();
      dispatch(updateMessageReadStatus({
        messageId: updatedMessage._id,
        readAt: updatedMessage.readAt
      }));

      // Emit socket event
      if (socketService.socket) {
        socketService.socket.emit('messageRead', {
          messageId,
          selectedUserId: selectedUser.userId,
          isChatFocused
        });
      }
    } catch (error) {
      console.error('Error marking message as read:', error);
    }
  }, [user, selectedUser, socketInitialized, isChatFocused, dispatch]);

  const handleSearch = async (query) => {
    setSearchQuery(query);
    
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (!query.trim()) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    // Add debounce to prevent too many requests
    searchTimeoutRef.current = setTimeout(async () => {
      setIsSearching(true);
      try {
        const token = localStorage.getItem('userToken');
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/users/search?query=${encodeURIComponent(query)}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (!response.ok) throw new Error('Search failed');
        
        const results = await response.json();
        setSearchResults(results);
      } catch (error) {
        console.error('Search error:', error);
        toast.error('Failed to search users');
      } finally {
        setIsSearching(false);
      }
    }, 300); // 300ms debounce
  };

  const handleSearchSelect = (selectedUser) => {
    handleUserSelect(selectedUser);
    setSearchQuery('');
    setSearchResults([]);
    // Add user to the chat list if not already present
    if (!users.some(u => u._id === selectedUser._id)) {
      setUsers(prev => [...prev, selectedUser]);
    }
  };

  // Effects
  useEffect(() => {
    const initializeChat = async () => {
      if (!user?._id) return;
      
      console.log('Starting chat initialization');
      const token = localStorage.getItem('userToken');
      if (!token) {
        console.log('No token available');
        return;
      }

      try {
        await initializeSocket();
        await loadChatUsers(); // Load chat users after socket is initialized
        console.log('Chat fully initialized');
      } catch (error) {
        console.error('Chat initialization failed:', error);
      }
    };

    initializeChat();
  }, [user, initializeSocket, loadChatUsers]);

  useEffect(() => {
    if (selectedUser && socketInitialized) {
      loadMessages();
    }
  }, [selectedUser, socketInitialized, loadMessages]);

  useEffect(() => {
    if (!socketInitialized || !user) return;

    const handleActiveUsers = (activeUsers) => {
      console.log('Received active users:', activeUsers); // Add logging
      const filteredUsers = activeUsers.filter(u => u._id !== user._id);
      dispatch(setActiveUsers(filteredUsers));
      
      // Update local users state with active status
      setUsers(prevUsers => prevUsers.map(u => ({
        ...u,
        isOnline: activeUsers.some(activeUser => activeUser._id === u._id)
      })));
    };

    const handleMessage = (message) => {
      // Add currentUserId to the message object
      const messageWithUser = {
        ...message,
        currentUserId: user._id
      };
      
      // Add the user to active chats if they're not already there
      const isNewChat = !users.some(u => u._id === (message.sender === user._id ? message.receiver : message.sender));
      if (isNewChat) {
        const newChatUser = {
          _id: message.sender === user._id ? message.receiver : message.sender,
          username: message.senderUsername || message.receiverUsername,
          status: 'online'
        };
        setUsers(prevUsers => [...prevUsers, newChatUser]);
      }

      dispatch(addMessage(messageWithUser));
      
      if ((message.sender === user._id && message.receiver === selectedUser?.userId) || 
          (message.sender === selectedUser?.userId && message.receiver === user._id)) {
        setTimeout(scrollToBottom, 100);
        
        // Mark as read only if it's a received message and chat is focused
        if (message.sender === selectedUser?.userId && !message.read && isChatFocused && document.hasFocus()) {
          markMessageAsRead(message._id);
        }
      }
    };

    const handleTyping = (data) => {
      if (data.senderId === selectedUser?.userId) {
        setIsTyping(data);
        // Clear typing indicator after 3 seconds
        setTimeout(() => setIsTyping(null), 3000);
      }
    };

    const handleMessageRead = (data) => {
      const { messageId, readAt } = data;
      console.log('Received messageRead event:', { messageId, readAt });
      dispatch(updateMessageReadStatus({ messageId, readAt }));
    };

    const socket = socketService.socket;
    if (socket) {
      socket.on('activeUsers', handleActiveUsers);
      socket.on('message', handleMessage);
      socket.on('typing', handleTyping);
      socket.on('messageRead', handleMessageRead);

      return () => {
        socket.off('activeUsers', handleActiveUsers);
        socket.off('message', handleMessage);
        socket.off('typing', handleTyping);
        socket.off('messageRead', handleMessageRead);
      };
    }
  }, [dispatch, user, selectedUser, socketInitialized, scrollToBottom, isChatFocused, markMessageAsRead, users]);

  // Handle window focus
  useEffect(() => {
    const handleFocus = () => {
      setIsChatFocused(true);
      
      if (selectedUser) {
        dispatch(clearUnreadMessages(selectedUser.userId));
        
        if (currentMessages.length > 0) {
          const unreadMessages = currentMessages.filter(
            message => 
              message.sender === selectedUser.userId && 
              !message.read
          );
          
          unreadMessages.forEach(message => {
            markMessageAsRead(message._id);
          });
        }
      }
    };

    const handleBlur = () => {
      setIsChatFocused(false);
    };

    // Initial check when component mounts or messages/selectedUser changes
    if (document.hasFocus() && selectedUser && currentMessages.length > 0) {
      dispatch(clearUnreadMessages(selectedUser.userId));
      const unreadMessages = currentMessages.filter(
        message => message.sender === selectedUser.userId && !message.read
      );
      unreadMessages.forEach(message => {
        markMessageAsRead(message._id);
      });
    }

    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);

    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
    };
  }, [selectedUser, currentMessages, markMessageAsRead, dispatch]);

  // Handle new messages
  useEffect(() => {
    if (!socketInitialized || !user) return;

    const handleMessage = (message) => {
      // Add currentUserId to the message object
      const messageWithUser = {
        ...message,
        currentUserId: user._id
      };
      
      // Add the user to active chats if they're not already there
      const isNewChat = !users.some(u => u._id === (message.sender === user._id ? message.receiver : message.sender));
      if (isNewChat) {
        const newChatUser = {
          _id: message.sender === user._id ? message.receiver : message.sender,
          username: message.senderUsername || message.receiverUsername,
          status: 'online'
        };
        setUsers(prevUsers => [...prevUsers, newChatUser]);
      }

      // Check if message belongs to current chat
      const isCurrentChat = 
        (message.sender === selectedUser?.userId && message.receiver === user._id) ||
        (message.receiver === selectedUser?.userId && message.sender === user._id);

      // Always dispatch to handle unread counts
      dispatch(addMessage(messageWithUser));
      
      // Only scroll if message belongs to current chat
      if (isCurrentChat) {
        setTimeout(scrollToBottom, 100);
        
        // Mark as read only if it's a received message and chat is focused
        if (message.sender === selectedUser?.userId && !message.read && isChatFocused && document.hasFocus()) {
          markMessageAsRead(message._id);
        }
      }
    };

    const handleMessageRead = (data) => {
      const { messageId, readAt } = data;
      dispatch(updateMessageReadStatus({ messageId, readAt }));
    };

    socketService.socket.on('message', handleMessage);
    socketService.socket.on('messageRead', handleMessageRead);

    // Return a cleanup function that checks if socket exists
    return () => {
      // Only attempt cleanup if we still have a socket
      if (socketService.socket) {
        console.log('Cleaning up socket listeners in effect');
        socketService.socket.off('message', handleMessage);
        socketService.socket.off('messageRead', handleMessageRead);
      }
    };
  }, [socketInitialized, user, selectedUser, isChatFocused, dispatch, markMessageAsRead, scrollToBottom, users]);

  useEffect(() => {
    return () => {
      if (socketInitialized && socketService.socket) {
        console.log('Cleaning up socket connection');
        socketService.disconnect();
        setSocketInitialized(false);
        setIsUserInitialized(false);
      }
    };
  }, [socketInitialized]);

  useEffect(() => {
    const handleFocus = () => setIsChatFocused(true);
    const handleBlur = () => setIsChatFocused(false);

    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);

    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
    };
  }, []);

  // Update the message rendering with tooltip
  const MessageComponent = ({ message }) => {
    return (
      <div className={`message ${message.sender === user?._id ? 'sent' : 'received'}`}>
        <div className="message-content">
          {message.content}
        </div>
        {message.sender === user?._id && (
          <div className="message-status">
            <div className="status-line">
              <span>{message.read ? 'Read' : 'Delivered'}</span>
              {message.read && message.readAt && (
                <span className="read-time">• {formatTime(message.readAt)}</span>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="chat-container">
      <div className="users-sidebar">
        <div className="users-header">
          <div className="user-avatar" title={`Logged in as ${user?.username}`}>
            {user?.username?.charAt(0).toUpperCase()}
          </div>
          <h3 className="app-title">DonkChat</h3>
          <button className="logout-button" onClick={handleLogout}>
            <FiLogOut size={20} />
          </button>
        </div>
        <div className="search-container">
          <div className="search-input-wrapper">
            <IoSearch className="search-icon" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="Search users..."
              className="search-input"
            />
          </div>
          {searchResults.length > 0 && searchQuery && (
            <div className="search-results">
              {searchResults.map(user => (
                <div
                  key={user._id}
                  className="search-result-item"
                  onClick={() => handleSearchSelect(user)}
                >
                  <span>{user.username}</span>
                </div>
              ))}
            </div>
          )}
          {isSearching && (
            <div className="search-results">
              <div className="search-loading">Searching...</div>
            </div>
          )}
        </div>
        <div className="users-list">
          {users.map(u => (
            <div
              key={u._id}
              className={`user-item ${selectedUser?.userId === u._id ? 'active' : ''}`}
              onClick={() => handleUserSelect(u)}
            >
              <div className="user-info">
                <span className="username">{u.username}</span>
                <div className="user-meta">
                  {unreadMessages[u._id] > 0 && (
                    <span className="unread-badge">{unreadMessages[u._id]}</span>
                  )}
                  <span className={`status-indicator ${u.status}`} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="chat-main">
        <div className="messages-wrapper">
          {selectedUser ? (
            <>
              {isLoadingMessages ? (
                <div className="loading-messages">
                  <p>Loading messages...</p>
                </div>
              ) : (
                <div className="messages-container">
                  {currentMessages.map(message => (
                    <MessageComponent key={message._id} message={message} />
                  ))}
                  {isTyping && (
                    <div className="typing-indicator">
                      {isTyping.username} is typing...
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>
              )}
              <form onSubmit={handleSubmit} className="message-form">
                <input
                  type="text"
                  value={messageInput}
                  onChange={handleTypingInput}
                  placeholder="Type a message..."
                />
                <button type="submit" disabled={!messageInput.trim()}>
                  <IoSend size={20} />
                </button>
              </form>
            </>
          ) : (
            <div className="no-chat-selected">
              <p>Select a user to start chatting</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Chat;
